import React, { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import RichTextEditor from './RichTextEditor';

const SpiritualContent = () => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    contentType: 'reflection',
    fullContent: '',
    imageUrl: '',
    displayOrder: 0,
    isActive: true
  });
  const [formLoading, setFormLoading] = useState(false);

  const { authenticatedFetch } = useAdminAuth();

  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/content/admin/all');
      const data = await response.json();
      
      if (data.success) {
        setContent(data.content);
        setError('');
      } else {
        setError('Error al cargar contenido');
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleCreateNew = () => {
    setEditingContent(null);
    setFormData({
      title: '',
      description: '',
      category: 'general',
      contentType: 'reflection',
      fullContent: '',
      imageUrl: '',
      displayOrder: 0,
      isActive: true
    });
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditingContent(item);
    setFormData({
      title: item.title,
      description: item.description,
      category: item.category,
      contentType: item.content_type,
      fullContent: item.full_content || '',
      imageUrl: item.image_url || '',
      displayOrder: item.display_order,
      isActive: item.is_active
    });
    setShowForm(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const url = editingContent 
        ? `/api/content/admin/${editingContent.id}`
        : '/api/content/admin/create';
      
      const method = editingContent ? 'PUT' : 'POST';

      const response = await authenticatedFetch(url, {
        method: method,
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setShowForm(false);
        await fetchContent();
        alert(editingContent ? 'Contenido actualizado exitosamente' : 'Contenido creado exitosamente');
      } else {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Error de conexión');
    } finally {
      setFormLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const toggleContentStatus = async (contentId) => {
    try {
      const response = await authenticatedFetch(`/api/content/admin/${contentId}/toggle`, {
        method: 'PATCH'
      });
      
      const data = await response.json();
      
      if (data.success) {
        await fetchContent();
      } else {
        alert('Error al cambiar estado del contenido');
      }
    } catch (error) {
      console.error('Error toggling content:', error);
      alert('Error de conexión');
    }
  };

  const deleteContent = async (contentId, contentTitle) => {
    if (!window.confirm(`¿Estás seguro de eliminar "${contentTitle}"?`)) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/content/admin/${contentId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        await fetchContent();
      } else {
        alert('Error al eliminar contenido');
      }
    } catch (error) {
      console.error('Error deleting content:', error);
      alert('Error de conexión');
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e2e8f0',
          borderTop: '4px solid #667eea',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#718096', marginTop: '16px' }}>
          Cargando contenido...
        </p>
      </div>
    );
  }

  return (
    <main style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '30px 20px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px'
      }}>
        <h2 style={{
          color: '#4a5568',
          margin: '0',
          fontSize: '1.5em',
          fontWeight: '600'
        }}>
          Contenido Espiritual ({content.length})
        </h2>
        <button 
          onClick={handleCreateNew}
          style={{
            background: '#48bb78',
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#38a169';
            e.target.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#48bb78';
            e.target.style.transform = 'none';
          }}
        >
          + Nuevo Contenido
        </button>
      </div>

      {error && (
        <div style={{
          background: '#fed7d7',
          border: '1px solid #feb2b2',
          color: '#c53030',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {/* Modal de formulario */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: '1000',
          padding: '20px',
          overflowY: 'auto'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              color: '#4a5568',
              fontSize: '1.4em'
            }}>
              {editingContent ? 'Editar Contenido' : 'Crear Nuevo Contenido'}
            </h3>

            <form onSubmit={handleFormSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#4a5568'
                }}>
                  Título *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '1em'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#4a5568'
                }}>
                  Descripción breve
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  placeholder="Descripción breve del contenido"
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '1em',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '15px',
                marginBottom: '20px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#4a5568'
                  }}>
                    Categoría *
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '1em'
                    }}
                  >
                    <option value="salud">Salud</option>
                    <option value="paz">Paz</option>
                    <option value="general">General</option>
                    <option value="familia">Familia</option>
                    <option value="trabajo">Trabajo</option>
                  </select>
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#4a5568'
                  }}>
                    Tipo de contenido
                  </label>
                  <select
                    name="contentType"
                    value={formData.contentType}
                    onChange={handleFormChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '1em'
                    }}
                  >
                    <option value="reflection">Reflexión</option>
                    <option value="prayer">Oración</option>
                    <option value="scripture">Escritura</option>
                    <option value="testimony">Testimonio</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#4a5568'
                }}>
                  Contenido completo
                </label>
                <RichTextEditor
                  value={formData.fullContent}
                  onChange={(html) => setFormData({...formData, fullContent: html})}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#4a5568'
                }}>
                  URL de imagen
                </label>
                <input
                  type="url"
                  name="imageUrl"
                  value={formData.imageUrl}
                  onChange={handleFormChange}
                  placeholder="https://ejemplo.com/imagen.jpg"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '1em'
                  }}
                />
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '15px',
                marginBottom: '30px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#4a5568'
                  }}>
                    Orden de visualización
                  </label>
                  <input
                    type="number"
                    name="displayOrder"
                    value={formData.displayOrder}
                    onChange={handleFormChange}
                    min="0"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '1em'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', paddingTop: '28px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleFormChange}
                    />
                    <span style={{ fontWeight: '600', color: '#4a5568' }}>Activo</span>
                  </label>
                </div>
              </div>

              <div style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  style={{
                    padding: '10px 20px',
                    background: formLoading ? '#a0aec0' : '#48bb78',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: formLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {formLoading ? 'Guardando...' : (editingContent ? 'Actualizar' : 'Crear')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lista de contenido */}
      {content.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: 'white',
          borderRadius: '12px',
          border: '2px dashed #e2e8f0'
        }}>
          <p style={{
            color: '#718096',
            fontSize: '1.1em',
            marginBottom: '20px'
          }}>
            No hay contenido espiritual creado
          </p>
          <button 
            onClick={handleCreateNew}
            style={{
              background: '#667eea',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Crear primer contenido
          </button>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '20px'
        }}>
          {content.map((item) => (
            <div 
              key={item.id} 
              style={{
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                opacity: item.is_active ? 1 : 0.7
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {item.image_url && (
                <div style={{
                  width: '100%',
                  height: '180px',
                  backgroundImage: `url(${item.image_url})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}></div>
              )}

              <div style={{
                padding: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '15px'
              }}>
                <h3 style={{
                  color: '#4a5568',
                  margin: '0',
                  fontSize: '1.2em',
                  fontWeight: '600',
                  lineHeight: '1.3',
                  flex: '1'
                }}>
                  {item.title}
                </h3>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '0.75em',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  background: item.is_active ? '#c6f6d5' : '#fed7d7',
                  color: item.is_active ? '#22543d' : '#742a2a'
                }}>
                  {item.is_active ? 'Activo' : 'Inactivo'}
                </span>
              </div>

              <div style={{ padding: '0 20px 20px' }}>
                <p style={{
                  color: '#718096',
                  margin: '0 0 15px 0',
                  lineHeight: '1.5',
                  fontSize: '0.95em'
                }}>
                  {item.description}
                </p>
                
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  marginBottom: '15px',
                  fontSize: '0.85em'
                }}>
                  <span style={{
                    padding: '4px 8px',
                    background: '#edf2f7',
                    borderRadius: '4px',
                    color: '#4a5568'
                  }}>
                    {item.category}
                  </span>
                  <span style={{
                    padding: '4px 8px',
                    background: '#e6fffa',
                    borderRadius: '4px',
                    color: '#234e52'
                  }}>
                    {item.content_type}
                  </span>
                  <span style={{
                    padding: '4px 8px',
                    background: '#fef5e7',
                    borderRadius: '4px',
                    color: '#744210'
                  }}>
                    Orden: {item.display_order}
                  </span>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap'
                }}>
                  <button 
                    onClick={() => handleEdit(item)}
                    style={{
                      padding: '6px 12px',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.85em',
                      fontWeight: '500',
                      background: '#667eea',
                      color: 'white'
                    }}
                  >
                    Editar
                  </button>
                  <button 
                    onClick={() => toggleContentStatus(item.id)}
                    style={{
                      padding: '6px 12px',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.85em',
                      fontWeight: '500',
                      background: item.is_active ? '#ed8936' : '#48bb78',
                      color: 'white'
                    }}
                  >
                    {item.is_active ? 'Desactivar' : 'Activar'}
                  </button>
                  <button 
                    onClick={() => deleteContent(item.id, item.title)}
                    style={{
                      padding: '6px 12px',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.85em',
                      fontWeight: '500',
                      background: '#e53e3e',
                      color: 'white'
                    }}
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
};

export default SpiritualContent;